import * as React from "react";
import { Box, AlertColor } from "@mui/material";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { CclPersistantDataGridMiscButton } from "../../../../../components/common/cclPersistentDataGrid/cclPersistentDataGrid";
import CclDataGridToolbar from "../../../../../components/common/cclDataGridToolbar/cclDataGridToolbar";
import CclAlertSnackbar from "../../../../../components/common/cclAlertSnackbar";
import ModifyTrainerEntryDrawer from "../modifyTrainerEntryDrawer";
import { cclTokenedPayrollApi } from "../../../../../services/cclTokenedPayrollApi";
import { useGetActiveResourcesQuery } from "../../../../../services/cclTokenedEnterpriseCoachingApi";
import { TrainerEntryFormValues } from "../modifyTrainerEntryDrawer";
import useLogAccessEvent from "../../../../../hooks/useLogAccessEvent";
import {  AccessEventOFCReducedDetails } from "../../../../../services/types/accessEventTypes";

export interface TrainerEntryDataGridProps {
    projectCode: string;
    sessionId: string;
    workEntryData?: any;
}

type PaymentDetailRow = {
    paymentId?: string | number;
    eventTrainer?: string | number;
    imKey?: string | number;
    rateCode?: string;
    glCode?: string;
    glcode?: string;
    GLCode?: string;
    gl_code?: string;
    units?: string | number;
    hours?: string | number;
    fee?: string | number;
};

const TrainerEntryDataGrid: React.FC<TrainerEntryDataGridProps> = (props) => {
    
    const [openModifyDrawer, setOpenModifyDrawer] = React.useState<boolean>(false);
    const [newEntry, setNewEntry] = React.useState<boolean>(true);
    const [selectedRows, setSelectedRows] = React.useState<Array<string | number>>([]);
    const [selectedTrainer, setSelectedTrainer] = React.useState<TrainerEntryFormValues | undefined>(undefined);

    // Snackbar state
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor | undefined>("info");

    const { data: activeResources } = useGetActiveResourcesQuery();

    const eventKey = React.useMemo<number>(() => {
        const parsed = Number(
            props.workEntryData?.eventKey ??
            props.workEntryData?.eventId ??
            props.workEntryData?.id ??
            0,
        );
        if (!Number.isFinite(parsed) || parsed <= 0) {
            return 0;
        }

        return parsed;
    }, [props.workEntryData]);

    const { data: paymentDetailsData = [], isLoading: paymentDetailsLoading } =
        cclTokenedPayrollApi.useGetPaymentDetailsbyEventKeyQuery(String(eventKey), {
            skip: eventKey <= 0,
        });

    const [deletePaymentDetail, { isLoading: isDeleting }] =
        cclTokenedPayrollApi.useDeletePaymentDetailMutation();
    const { logEvent } = useLogAccessEvent();

    const isExactlyOneSelected = selectedRows.length === 1;

    const showSnackbarMessage = React.useCallback((message: string, severity: AlertColor) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setShowSnackbar(true);
    }, []);

    const resolveGlCode = (row: PaymentDetailRow): string => {
        return String(row.glCode ?? row.glcode ?? row.GLCode ?? row.gl_code ?? "");
    };

    const resolveTrainerName = (trainerValue: any)=> {
        const trainerImKey = Number(trainerValue);
        if (!Number.isFinite(trainerImKey) || !activeResources) {
            return "";
        }

        let matchedResource;

        for (let i = 0; i < activeResources.length; i++) {
            const resource = activeResources[i];
            const resourceImKey = resource?.imkey;
            if (resourceImKey === trainerImKey) {
                matchedResource = resource;
                break;
            }
        }
        
        if (!matchedResource) {
            return "";
        }
        const firstname = matchedResource.firstname;
        const lastname = matchedResource.lastname;
        return `${firstname} ${lastname}`.trim();
    };

    const TrainerEntryColumns = [
        {
            field: "imKey",
            type: "string",
            headerName: 'Faculty',
            width: 150,
            valueGetter: (params: any) => {
                return resolveTrainerName(params?.row?.imKey);
            },
        },
        {
            field: "glCode",
            type: "string",
            headerName: "GL Code",
            width: 120
        },
        {
            field: "rateCode",
            type: "string",
            headerName: "Rate Code",
            width: 120
        },
        {
            field: "units",
            type: "number",
            headerName: "Units",
            width: 100,
        },
        {
            field: "hours",
            type: "number",
            headerName: "Hours",
            width: 100,
        },
        {
            field: "fee",
            type: "number",
            headerName: "Fee",
            width: 120
        }
    ];

    const trainerEntryButtons: CclPersistantDataGridMiscButton[] = [
        {
            id: "add-trainer-entry",
            caption: "Add Faculty Entry",
            onClick: () => {},
            returnSelected: true,
            restricted: false,
            loadingBtn: true,
            disable: eventKey <= 0,
        },
        {
            id: "edit-trainer-entry",
            caption: "Edit Faculty Entry",
            onClick: () => {},
            returnSelected: true,
            restricted: false,
            disable: !isExactlyOneSelected,
        },
        {
            id: "delete-trainer-entry",
            caption: "Delete Faculty Entry",
            onClick: () => {},
            returnSelected: true,
            restricted: false,
            loadingBtn: true,
            disable: !isExactlyOneSelected || isDeleting,
        },
    ];

    const miscButtonClick = (id: string) => {
        switch(id) {
            case "add-trainer-entry":
                setNewEntry(true);
                setSelectedTrainer(undefined);
                setOpenModifyDrawer(true);
                break;
            case "edit-trainer-entry":
                const selectedRowId = selectedRows[0];
                const selectedPaymentDetail = (paymentDetailsData || []).find(
                    (row: PaymentDetailRow) =>
                        row.paymentId === selectedRowId ||
                        `${row.eventTrainer ?? ""}-${row.rateCode ?? ""}-${resolveGlCode(row)}` === selectedRowId,
                );

                if (!selectedPaymentDetail) {
                    break;
                }

                setNewEntry(false);
                setSelectedTrainer({
                    paymentId: selectedPaymentDetail.paymentId,
                    eventTrainer: Number(selectedPaymentDetail.imKey),
                    trainerName: resolveTrainerName(selectedPaymentDetail.imKey),
                    glCode: resolveGlCode(selectedPaymentDetail),
                    rateCode: String(selectedPaymentDetail.rateCode ?? ""),
                    units: String(selectedPaymentDetail.units ?? ""),
                    hours: String(selectedPaymentDetail.hours ?? ""),
                    fee: String(selectedPaymentDetail.fee ?? ""),
                });
                setOpenModifyDrawer(true);
                break;
            case "delete-trainer-entry":
                const deleteRowId = selectedRows[0];
                const paymentDetailToDelete = (paymentDetailsData || []).find(
                    (row: PaymentDetailRow) =>
                        row.paymentId === deleteRowId ||
                        `${row.eventTrainer ?? ""}-${row.rateCode ?? ""}-${resolveGlCode(row)}` === deleteRowId,
                );

                if (!paymentDetailToDelete?.paymentId) {
                    console.error("Cannot delete payment detail without paymentId");
                    break;
                }

                deletePaymentDetail(String(paymentDetailToDelete.paymentId))
                    .unwrap()
                    .then(() => {
                        const evtData: AccessEventOFCReducedDetails = {
                            sessionID: props.sessionId,
                            projectCode: props.projectCode,
                        };
                        logEvent("PaymentDetailDeleted", evtData);
                        setSelectedRows([]);
                        
                        // Show success snackbar
                        setSnackbarMessage("Faculty Entry Deleted");
                        setSnackbarSeverity("success");
                        setShowSnackbar(true);
                    })
                    .catch((error) => {
                        console.error("Failed to delete faculty entry:", error);
                        
                        // Show error snackbar
                        setSnackbarMessage("Failed to delete Faculty Entry");
                        setSnackbarSeverity("error");
                        setShowSnackbar(true);
                    });
                break;
            default:
                break;
        }
    }

    return (
        <Box sx={{height: 1}}>
            {openModifyDrawer && (
                <ModifyTrainerEntryDrawer
                    sessionId={props.sessionId}
                    projectCode={props.projectCode}
                    eventKey={eventKey}
                    newEntry={newEntry}
                    trainer={selectedTrainer}
                    open={openModifyDrawer}
                    onClose={() => setOpenModifyDrawer(false)}
                    onShowSnackbar={showSnackbarMessage}
                />
            )}
            
            <CclAlertSnackbar
                open={showSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={() => setShowSnackbar(false)}
            />
            
            <DataGridPremium 
                columns={TrainerEntryColumns} 
                rows={paymentDetailsData || []}
                loading={paymentDetailsLoading}
                getRowId={(row: PaymentDetailRow) => row.paymentId ?? `${row.eventTrainer ?? ""}-${row.rateCode ?? ""}-${resolveGlCode(row)}`}
                checkboxSelection
                disableSelectionOnClick
                onSelectionModelChange={(newSelection: any) => setSelectedRows(newSelection as Array<string | number>)}
                components={{Toolbar: CclDataGridToolbar}}
                componentsProps={{
                    toolbar: {
                        miscButtons: trainerEntryButtons,
                        btnClick: miscButtonClick,
                        hideSettingsButtons: true
                    }
                }}
                autoHeight 
            />
        </Box> 
    );
};

export default TrainerEntryDataGrid;
